/**
 * The looksmax glossary: 31 terms, five languages.
 *
 * ── Why these pages, and why in this shape ───────────────────────────────────
 *
 * 99.9% of AI Overviews appear on informational queries (docs/SEO-GEO-RESEARCH.md,
 * finding 5), and "what is canthal tilt" is the purest informational query this
 * product has. A definition page whose H1 is the question and whose first
 * paragraph is the answer is the exact shape that gets extracted.
 *
 * Twenty-one of these already exist. The live v1 site publishes them at these
 * URLs, in these five languages, and they are indexed today — so that part is a
 * restoration rather than an expansion, which is why it came before the
 * templated long tail despite the publish-slowly finding.
 *
 * The other ten are new, and they are the one place where expanding beats
 * waiting. Each is its own keyword with its own page — a glossary has no
 * cannibalisation problem, which is exactly why the alternative expansion
 * (a page per measurement band: "canthal tilt 5 degrees", "high fWHR") was
 * rejected. Those would have been a third page competing with the tool and the
 * glossary entry for the same term, which is finding 11 in reverse.
 *
 * Four of the ten exist mainly because nobody else states them honestly.
 * `bone-smashing` is a practice that breaks facial bone on purpose and the
 * search results for it are mostly instructions; `mogging` and `halo-effect`
 * are the vocabulary the whole category runs on and are usually defined by
 * people selling something; `buccal-fat` is a surgical decision that
 * fifteen-year-olds are searching for. A glossary that skipped those because
 * they are uncomfortable would be a glossary that only covered the easy half.
 *
 * ── The one rule ─────────────────────────────────────────────────────────────
 *
 * A glossary entry states what a term MEANS and, where the site can measure it,
 * links to the tool that does. It does not tell anyone what their number should
 * be — the tools do that, with the target named — and it does not claim a
 * convention is a finding. Several of these terms come with folk claims
 * attached ("mewing changes adult bone", "PSL is a scale"); the entry says
 * plainly what is and is not supported, because a glossary that repeats the
 * folklore is worse than no glossary.
 */

import type { ToolSlug } from "../tools/types";

export const GLOSSARY_SLUGS = [
  "bone-smashing",
  "buccal-fat",
  "canthal-tilt",
  "e-line",
  "face-shape-test",
  "face-symmetry",
  "facial-thirds",
  "fwhr",
  "golden-ratio",
  "gonial-angle",
  "halo-effect",
  "hunter-eyes",
  "intercanthal-distance",
  "jawline-score",
  "looksmaxxing",
  "maxilla",
  "mewing",
  "mewing-chart",
  "midface-ratio",
  "mogging",
  "negative-canthal-tilt",
  "neoteny",
  "ogee-curve",
  "orbital-vector",
  "philtrum",
  "positive-canthal-tilt",
  "prey-eyes",
  "psl-score",
  "sexual-dimorphism",
  "submental-cervical-angle",
  "upper-eyelid-exposure",
] as const;

export type GlossarySlug = (typeof GLOSSARY_SLUGS)[number];

export interface GlossaryEntry {
  /** The term itself, as a reader would say it. Becomes the H1. */
  readonly term: string;
  readonly title: string;
  readonly description: string;
  /** One grey line under the H1. Never a second paragraph. */
  readonly standfirst: string;
  /** 134–167 words, self-contained, definition first. */
  readonly answer: string;
  /**
   * The tool that measures this, if there is one.
   *
   * Most of these have no tool, and that is stated rather than papered over:
   * those entries end with the glossary rather than with a link to something
   * that does not measure what the page is about.
   */
  readonly tool?: ToolSlug;
  /** Two or three related terms. Hand-picked, not derived — see index.ts. */
  readonly seeAlso: readonly GlossarySlug[];
}

/** The shape of one language's bundle. */
export type GlossaryBundle = Record<GlossarySlug, GlossaryEntry>;
